import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Copy, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  ExternalLink,
  Trash2,
  Loader2,
  Zap
} from 'lucide-react';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface ForgeIntegrationSettingsProps {
  accessToken: string;
  companyId?: string;
  companyName?: string;
  firmId?: string;
}

interface ForgeStatus {
  connected: boolean;
  jiraCloudId: string | null;
  lastUsed: string | null;
  latestScan: {
    jobId: string;
    timestamp: string;
    metadata: {
      totalProjects: number;
      totalCustomFields: number;
      totalRequestTypes: number;
      totalWorkflows: number;
      totalUsers: number;
    };
  } | null;
}

export function ForgeIntegrationSettings({ 
  accessToken, 
  companyId,
  companyName,
  firmId
}: ForgeIntegrationSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<ForgeStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadStatus();
  }, [accessToken]);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/forge/status`;
      
      if (companyId) {
        url += `?companyId=${encodeURIComponent(companyId)}`;
      } else if (firmId) {
        url += `?firmId=${encodeURIComponent(firmId)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load Forge connection status');
      }
    } catch (err: any) {
      console.error('[FORGE] Error loading status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const requestBody: any = {};
      if (companyId) {
        requestBody.companyId = companyId;
      } else if (firmId) {
        requestBody.firmId = firmId;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/forge/generate-token`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: Object.keys(requestBody).length > 0 ? JSON.stringify(requestBody) : undefined
        }
      );

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        await loadStatus(); // Refresh status
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate connection token');
      }
    } catch (err: any) {
      console.error('[FORGE] Error generating token:', err);
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const revokeToken = async () => {
    if (!confirm('Are you sure you want to disconnect the Forge app? You will need to regenerate a token to reconnect.')) {
      return;
    }

    setRevoking(true);
    setError(null);
    
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/forge/token`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setToken(null);
        await loadStatus(); // Refresh status
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to revoke connection token');
      }
    } catch (err: any) {
      console.error('[FORGE] Error revoking token:', err);
      setError(err.message);
    } finally {
      setRevoking(false);
    }
  };

  const copyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Atlassian Forge Integration
          </CardTitle>
          <CardDescription>
            Connect your Jira instance with PathKit's marketplace app
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Atlassian Forge Integration
        </CardTitle>
        <CardDescription>
          Connect your Jira instance with PathKit's marketplace app for automated configuration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">Connection Status:</span>
              {status?.connected ? (
                <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="outline">
                  Not Connected
                </Badge>
              )}
            </div>
            
            {status?.connected && status.jiraCloudId && (
              <p className="text-sm text-muted-foreground">
                Jira Cloud ID: {status.jiraCloudId}
              </p>
            )}
            
            {status?.connected && status.lastUsed && (
              <p className="text-sm text-muted-foreground">
                Last used: {new Date(status.lastUsed).toLocaleString()}
              </p>
            )}
          </div>

          {status?.connected && (
            <Button
              variant="outline"
              size="sm"
              onClick={revokeToken}
              disabled={revoking}
            >
              {revoking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Disconnect
                </>
              )}
            </Button>
          )}
        </div>

        {/* Latest Scan Info */}
        {status?.latestScan && (
          <div className="p-4 border rounded-lg bg-muted/50">
            <h4 className="font-medium mb-2">Latest Discovery Scan</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Date:</span>{' '}
                <span className="font-medium">
                  {new Date(status.latestScan.timestamp).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Projects:</span>{' '}
                <span className="font-medium">{status.latestScan.metadata.totalProjects}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Custom Fields:</span>{' '}
                <span className="font-medium">{status.latestScan.metadata.totalCustomFields}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Request Types:</span>{' '}
                <span className="font-medium">{status.latestScan.metadata.totalRequestTypes}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Workflows:</span>{' '}
                <span className="font-medium">{status.latestScan.metadata.totalWorkflows}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Users:</span>{' '}
                <span className="font-medium">{status.latestScan.metadata.totalUsers}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Token Generation */}
        {!status?.connected && (
          <div className="space-y-4">
            {!token ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Generate a connection token to connect the PathKit Forge app from the Atlassian Marketplace.
                </p>
                <Button onClick={generateToken} disabled={generating}>
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Generate Connection Token
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Token generated. Copy it and paste into the Forge app.
                  </AlertDescription>
                </Alert>

                <div className="flex gap-2">
                  <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all">
                    {token}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToken}
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>

                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <h4 className="font-medium mb-2 text-blue-900">Next Steps:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                    <li>Install the PathKit Connector from Atlassian Marketplace</li>
                    <li>Open Jira Settings → Apps → PathKit Connector</li>
                    <li>Paste the token above into the connection field</li>
                    <li>Click "Connect to PathKit"</li>
                    <li>Run a discovery scan to analyze your Jira configuration</li>
                  </ol>
                </div>

                <Button
                  variant="link"
                  className="p-0 h-auto"
                  onClick={() => window.open('https://marketplace.atlassian.com/apps/scopify-connector', '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Atlassian Marketplace
                </Button>
              </div>
            )}
          </div>
        )}

        {/* What is Forge Integration */}
        <div className="p-4 border rounded-lg bg-muted/30">
          <h4 className="font-medium mb-2">What is the Forge Integration?</h4>
          <p className="text-sm text-muted-foreground mb-3">
            The PathKit Connector is a free Atlassian Marketplace app that automates Jira configuration
            based on your PathKit recommendations.
          </p>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li><strong>Phase 1 (Current):</strong> Read-only discovery scan of your Jira instance</li>
            <li><strong>Phase 2 (Coming Soon):</strong> Automated project and field creation</li>
            <li><strong>Phase 3:</strong> Workflow automation and Confluence integration</li>
            <li><strong>Phase 4:</strong> Advanced automation rules and notifications</li>
          </ul>
        </div>

        {/* Refresh Status */}
        <div className="flex justify-between items-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadStatus}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Status
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}